import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CTBNotification,
  NotificationType,
  NotificationPriority,
  NotificationCategory
} from "../../../utils/api/endpoints/notificationsApi";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";
import {
  FiMoreVertical,
  FiCheckCircle,
  FiXCircle,
  FiShield,
  FiSettings,
  FiStar,
  FiClock,
  FiUsers,
  FiDollarSign,
  FiAlertTriangle,
  FiMessageCircle,
  FiBook,
  FiBell,
  FiShare2,
  FiExternalLink
} from "react-icons/fi";
import RichTextRenderer from "../../../components/common/RichTextRenderer";
import ConfirmationModal from "./ConfirmationModal";

interface NotificationCardProps {
  notification: CTBNotification;
  onMarkRead: (id: number) => void;
  onMarkUnread: (id: number) => void;
  onDelete: (id: number) => void;
  isSelected?: boolean;
  onSelectToggle?: (id: number) => void;
  onActionClicked?: (id: number) => void;
}

const getNotificationIcon = (type: NotificationType, priority: NotificationPriority) => {
  const iconClass = "w-5 h-5";
  const priorityColors = {
    [NotificationPriority.Low]: "text-gray-400 bg-gray-50",
    [NotificationPriority.Normal]: "text-blue-500 bg-blue-50",
    [NotificationPriority.High]: "text-amber-500 bg-amber-50",
    [NotificationPriority.Critical]: "text-red-500 bg-red-50"
  };

  const wrapperClass = `flex items-center justify-center w-10 h-10 rounded-full shrink-0 ${priorityColors[priority] || priorityColors[NotificationPriority.Normal]}`;

  let icon = <FiBell className={iconClass} />;
  switch (type) {
    case NotificationType.ReportApproved:
      icon = <FiCheckCircle className={`${iconClass} text-emerald-500`} />;
      break;
    case NotificationType.ReportRejected:
      icon = <FiXCircle className={`${iconClass} text-red-500`} />;
      break;
    case NotificationType.SecurityAlert:
      icon = <FiShield className={`${iconClass} text-red-500`} />;
      break;
    case NotificationType.SystemMaintenance:
      icon = <FiSettings className={`${iconClass} text-blue-500`} />;
      break;
    case NotificationType.FeatureUpdate:
      icon = <FiStar className={`${iconClass} text-[#58CC02]`} />;
      break;
    case NotificationType.Reminder:
      icon = <FiClock className={`${iconClass} text-amber-500`} />;
      break;
    case NotificationType.Collaboration:
      icon = <FiUsers className={`${iconClass} text-blue-500`} />;
      break;
    case NotificationType.Achievement:
      icon = <FiStar className={`${iconClass} text-amber-400`} />;
      break;
    case NotificationType.PaymentReminder:
      icon = <FiDollarSign className={`${iconClass} text-emerald-500`} />;
      break;
    case NotificationType.ReportDeadline:
      icon = <FiClock className={`${iconClass} text-red-500`} />;
      break;
    case NotificationType.BugReport:
      icon = <FiAlertTriangle className={`${iconClass} text-red-500`} />;
      break;
    case NotificationType.SupportTicket:
      icon = <FiMessageCircle className={`${iconClass} text-blue-500`} />;
      break;
    case NotificationType.ComplianceAlert:
      icon = <FiShield className={`${iconClass} text-red-500`} />;
      break;
    case NotificationType.Training:
      icon = <FiBook className={`${iconClass} text-[#58CC02]`} />;
      break;
  }

  return <div className={wrapperClass}>{icon}</div>;
};

const getPriorityBadge = (priority: NotificationPriority) => {
  const styles = {
    [NotificationPriority.Low]: "bg-gray-100 text-gray-800",
    [NotificationPriority.Normal]: "bg-[#58CC02]/[0.17] text-[#58CC02]",
    [NotificationPriority.High]: "bg-amber-100 text-amber-800",
    [NotificationPriority.Critical]: "bg-red-100 text-red-800"
  };

  const labels = {
    [NotificationPriority.Low]: "Low",
    [NotificationPriority.Normal]: "Normal",
    [NotificationPriority.High]: "High",
    [NotificationPriority.Critical]: "Critical"
  };

  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[priority] || styles[NotificationPriority.Normal]}`}>
      {labels[priority]}
    </span>
  );
};

const getCategoryBadge = (category: NotificationCategory) => {
  const styles = {
    [NotificationCategory.Security]: "bg-red-50 text-red-700 border-red-200",
    [NotificationCategory.System]: "bg-blue-50 text-blue-700 border-blue-200",
    [NotificationCategory.Business]: "bg-emerald-50 text-emerald-700 border-emerald-200",
    [NotificationCategory.Personal]: "bg-purple-50 text-purple-700 border-purple-200",
    [NotificationCategory.Collaboration]: "bg-indigo-50 text-indigo-700 border-indigo-200",
    [NotificationCategory.Compliance]: "bg-amber-50 text-amber-700 border-amber-200",
    [NotificationCategory.Training]: "bg-teal-50 text-teal-700 border-teal-200"
  };

  return (
    <span className={`px-2 py-0.5 rounded border text-xs font-semibold ${styles[category] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
      {category.charAt(0).toUpperCase() + category.slice(1)}
    </span>
  );
};

const getRoleBadge = (senderRole: string | number | undefined, category: NotificationCategory) => {
  if (senderRole === undefined || senderRole === null || senderRole === "") {
    return getCategoryBadge(category);
  }

  const roleStr = String(senderRole).toLowerCase();
  let roleKey = roleStr;

  // Map numeric UserRole values to string equivalents
  if (roleStr === "1") roleKey = "researcher";
  else if (["2", "6", "7", "8"].includes(roleStr)) roleKey = "business";
  else if (["3", "5", "9", "10"].includes(roleStr)) roleKey = "admin";
  else if (roleStr === "4") roleKey = "qa";

  if (["admin", "sub_admin", "admin_manager", "solutions_specialist"].includes(roleKey)) {
    return (
      <span className="px-2 py-0.5 rounded border text-xs font-semibold bg-indigo-50 text-indigo-700 border-indigo-200 animate-fade-in">
        Admin
      </span>
    );
  }

  if (["business", "business_manager", "business_administrator", "developer"].includes(roleKey)) {
    return (
      <span className="px-2 py-0.5 rounded border text-xs font-semibold bg-emerald-50 text-emerald-700 border-emerald-200 animate-fade-in">
        Business
      </span>
    );
  }

  if (roleKey === "researcher") {
    return (
      <span className="px-2 py-0.5 rounded border text-xs font-semibold bg-purple-50 text-purple-700 border-purple-200 animate-fade-in">
        Researcher
      </span>
    );
  }

  if (roleKey === "qa") {
    return (
      <span className="px-2 py-0.5 rounded border text-xs font-semibold bg-teal-50 text-teal-700 border-teal-200 animate-fade-in">
        QA
      </span>
    );
  }

  if (["system", "automation"].includes(roleKey)) {
    return (
      <span className="px-2 py-0.5 rounded border text-xs font-semibold bg-amber-50 text-amber-700 border-amber-200 animate-fade-in">
        System
      </span>
    );
  }

  return (
    <span className="px-2 py-0.5 rounded border text-xs font-semibold bg-gray-50 text-gray-700 border-gray-200 animate-fade-in">
      {roleStr.charAt(0).toUpperCase() + roleStr.slice(1)}
    </span>
  );
};

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onMarkRead,
  onMarkUnread,
  onDelete,
  isSelected = false,
  onSelectToggle,
  onActionClicked
}) => {
  const navigate = useNavigate();
  const { role } = useUserCredentials();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isReportNotification = [
    NotificationType.ReportInfo,
    NotificationType.ReportApproved,
    NotificationType.ReportRejected,
    NotificationType.Comments,
    NotificationType.Certificate
  ].includes(notification.type);

  const isRetest = (() => {
    if (notification.metadata) {
      try {
        const meta = typeof notification.metadata === "string"
          ? JSON.parse(notification.metadata)
          : notification.metadata;
        if (meta && meta.isRetest !== undefined) return Boolean(meta.isRetest);
      } catch (e) {
        // ignore
      }
    }
    const msg = (notification.message || "").toLowerCase();
    const title = (notification.title || "").toLowerCase();
    return (
      title.includes("retest") ||
      msg.includes("retest has been initiated") ||
      msg.includes("retest status") ||
      msg.includes("retest delegation") ||
      msg.includes("retest delegated") ||
      msg.includes("retest sla breached") ||
      msg.includes("a retest has been") ||
      msg.includes("retest")
    );
  })();

  // Helper to safely extract ID from JSON or raw payloads
  const getPayloadId = (payloadStr: string | number | undefined, key: string): string => {
    if (payloadStr === undefined || payloadStr === null) return "";
    const str = String(payloadStr);
    if (str.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(str);
        return parsed[key] !== undefined ? String(parsed[key]) : str;
      } catch (e) {
        // ignore
      }
    }
    return str;
  };

  // Determine standard redirection url
  let detailUrl = "/dashboard";
  const cleanPayloadId = String(notification.payload).trim();
  const isNumericPayload = cleanPayloadId && !isNaN(Number(cleanPayloadId));

  if (isRetest && isNumericPayload) {
    detailUrl = `/dashboard/retests/${cleanPayloadId}`;
  } else if (notification.type === NotificationType.ComplianceAlert) {
    let retestId = "";
    const fromPayloadKey = getPayloadId(notification.payload, "retest_id");
    if (fromPayloadKey && fromPayloadKey !== String(notification.payload)) {
      retestId = fromPayloadKey;
    } else {
      const fromPayloadKeyAlt = getPayloadId(notification.payload, "retestId");
      if (fromPayloadKeyAlt && fromPayloadKeyAlt !== String(notification.payload)) {
        retestId = fromPayloadKeyAlt;
      }
    }
    if (!retestId && notification.metadata) {
      try {
        const meta = typeof notification.metadata === "string"
          ? JSON.parse(notification.metadata)
          : notification.metadata;
        if (meta) {
          const val = meta.retestId || meta.retest_id;
          if (val) retestId = String(val);
        }
      } catch (e) {
        // ignore
      }
    }
    if (!retestId && isNumericPayload) {
      retestId = cleanPayloadId;
    }
    if (retestId) {
      detailUrl = `/dashboard/retests/${retestId}`;
    }
  } else if (isReportNotification || notification.type === NotificationType.Collaboration) {
    const reportIdFromPayload = getPayloadId(notification.payload, "report_id") || getPayloadId(notification.payload, "reportId");
    const repId = (reportIdFromPayload && reportIdFromPayload !== String(notification.payload)) ? reportIdFromPayload : cleanPayloadId;
    if (repId) {
      detailUrl = `/dashboard/reports/${repId}`;
    }
  } else if (notification.type === NotificationType.SecurityAlert) {
    const rawId = String(notification.payload || "").trim();
    const isJson = rawId.startsWith("{");
    let isVault = Boolean(
      (notification.title &&
        (notification.title.toLowerCase().includes("vault") ||
          notification.title.toLowerCase().includes("master password"))) ||
      (notification.message &&
        (notification.message.toLowerCase().includes("vault") ||
          notification.message.toLowerCase().includes("master password")))
    );
    let companyId: any = null;

    if (isJson) {
      try {
        const parsed = JSON.parse(rawId);
        if (
          parsed.vault_id ||
          (parsed.action &&
            typeof parsed.action === "string" &&
            (parsed.action.includes("vault") ||
              parsed.action.includes("master_password")))
        ) {
          isVault = true;
        }
        if (parsed.company_id || parsed.companyId) {
          companyId = parsed.company_id || parsed.companyId;
        }
      } catch (e) {
        // ignore
      }
    }

    if (isVault) {
      const isAdmin = role
        ? [UserRole.ADMIN, UserRole.ADMIN_MANAGER, UserRole.SUB_ADMIN].includes(role)
        : false;
      if (isAdmin && companyId) {
        detailUrl = `/dashboard/companies/${companyId}#vault`;
      } else {
        detailUrl = `/dashboard/vault`;
      }
    } else if (!isJson && rawId && !isNaN(Number(rawId))) {
      detailUrl = `/dashboard/reports/${rawId}`;
    }
  } else if (
    notification.type === NotificationType.Program ||
    notification.type === NotificationType.ScopeVerification ||
    notification.type === NotificationType.SystemMaintenance
  ) {
    const reportId = getPayloadId(notification.payload, "report_id");
    const programId = getPayloadId(notification.payload, "program_id");
    const runId = getPayloadId(notification.payload, "run_id");
    const vaultId = getPayloadId(notification.payload, "vault_id");
    const isJson = String(notification.payload).trim().startsWith("{");
    if (reportId && reportId !== String(notification.payload)) {
      detailUrl = `/dashboard/pentest-reports/${reportId}/edit`;
    } else if (isJson && programId && programId !== String(notification.payload)) {
      if (runId && runId !== String(notification.payload)) {
        detailUrl = `/dashboard/programs/${programId}?runId=${runId}&section=Scheduled Runs`;
      } else if (vaultId && vaultId !== String(notification.payload)) {
        detailUrl = `/dashboard/programs/${programId}?section=Credential Vault`;
      } else {
        detailUrl = `/dashboard/programs/${programId}`;
      }
    } else if (!isJson) {
      const rawId = String(notification.payload).trim();
      if (rawId && !isNaN(Number(rawId))) {
        detailUrl = `/dashboard/programs/${rawId}`;
      }
    }
  } else if (notification.type === NotificationType.Payments) {
    detailUrl = `/dashboard/payments`;
  } else if (notification.type === NotificationType.AssetCreated) {
    try {
      const parsedPayload = typeof notification.payload === "string"
        ? JSON.parse(notification.payload)
        : notification.payload;
      if (parsedPayload) {
        const assetId = parsedPayload.asset_id || parsedPayload.assetId;
        const programId = parsedPayload.program_id || parsedPayload.programId;
        const isResourceDiscovered = notification.title === "New Resource Discovered";

        const resourceId = parsedPayload.resourceId || parsedPayload.resource_id;

        if (isResourceDiscovered && (programId || assetId)) {
          const resourceQuery = resourceId ? `&resourceId=${resourceId}` : "";
          detailUrl = `/dashboard/programs/${programId || assetId}?section=Discovery${resourceQuery}`;
        } else {
          const companyId = parsedPayload.company_id || parsedPayload.companyId;
          const isAdmin = role
            ? [UserRole.ADMIN, UserRole.ADMIN_MANAGER, UserRole.SUB_ADMIN].includes(role)
            : false;
          if (isAdmin && companyId && assetId) {
            detailUrl = `/dashboard/companies/${companyId}/assets/${assetId}`;
          } else if (assetId) {
            detailUrl = `/dashboard/assets/${assetId}`;
          }
        }
      }
    } catch (e) {
      // ignore
    }
  }

  // Fallback check specifically for SecurityAlert notifications relating to vault / master password
  const isVaultNotification = Boolean(
    notification.type === NotificationType.SecurityAlert &&
      ((notification.title &&
        (notification.title.toLowerCase().includes("vault") ||
          notification.title.toLowerCase().includes("master password"))) ||
        (notification.message &&
          (notification.message.toLowerCase().includes("vault") ||
            notification.message.toLowerCase().includes("master password"))))
  );
  if (detailUrl === "/dashboard" && isVaultNotification) {
    detailUrl = "/dashboard/vault";
  }

  // Parse comment_id and sender_role if present in metadata
  let commentId: number | undefined;
  let senderRole: string | undefined;
  let reportTitle: string | undefined;
  let retestTitle: string | undefined;
  if (notification.metadata) {
    try {
      const meta = typeof notification.metadata === "string"
        ? JSON.parse(notification.metadata)
        : notification.metadata;
      commentId = meta.comment_id;
      senderRole = meta.sender_role || meta.senderRole;
      reportTitle = meta.report_title || meta.reportTitle;
      retestTitle = meta.retest_title || meta.retestTitle;
    } catch (e) {
      // ignore
    }
  }



  // Type-specific button clicks
  const handlePrimaryAction = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onActionClicked) {
      onActionClicked(notification.id);
    } else if (!notification.read) {
      onMarkRead(notification.id);
    }

    if (notification.type === NotificationType.Comments) {
      navigate(`${detailUrl}${commentId ? `#comment-${commentId}` : "#comments"}`);
    } else {
      navigate(detailUrl);
    }
  };

  const handleReplyAction = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onActionClicked) {
      onActionClicked(notification.id);
    } else if (!notification.read) {
      onMarkRead(notification.id);
    }
    navigate(`${detailUrl}#comments-box`);
  };

  return (
    <div
      className={`flex items-start gap-4 p-5 bg-white border border-gray-200 rounded-xl transition-all duration-200 hover:shadow-sm ${!notification.read ? "border-l-4 border-l-[#58CC02]" : ""
        }`}
    >
      {onSelectToggle && (
        <div className="flex items-center justify-center pt-2 shrink-0">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelectToggle(notification.id)}
            className="w-4 h-4 rounded text-[#010D2C] border-gray-300 focus:ring-[#010D2C] cursor-pointer accent-[#010D2C]"
          />
        </div>
      )}

      {/* Icon & Unread Indicator */}
      <div className="relative">
        {getNotificationIcon(notification.type, notification.priority)}
        {!notification.read && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 border-2 border-white rounded-full" />
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0">
        {/* Row 1: Title, Badges, Time, Menu */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={`font-semibold text-sm text-[#010D2C] ${!notification.read ? "font-bold text-gray-900" : "text-gray-700"}`}>
              {notification.title}
            </h3>
            {getPriorityBadge(notification.priority)}
            {getRoleBadge(senderRole, notification.category)}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">
              {notification.createdAt}
            </span>
            {/* Overflow 3-dot Menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              >
                <FiMoreVertical className="w-4 h-4" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-30">
                  {notification.read ? (
                    <button
                      onClick={() => {
                        onMarkUnread(notification.id);
                        setMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      Mark unread
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        onMarkRead(notification.id);
                        setMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      Mark read
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      setShowDeleteConfirm(true);
                    }}
                    className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Message Text */}
        <p className="text-sm text-gray-600 mt-2 font-medium leading-relaxed">
          {notification.message}
        </p>

        {/* Row 3: Rich Content */}
        {notification.rich_content &&
          notification.type !== NotificationType.AssetCreated &&
          notification.type !== NotificationType.Program &&
          notification.type !== NotificationType.SystemMaintenance &&
          notification.type !== NotificationType.ScopeVerification &&
          notification.type !== NotificationType.SecurityAlert && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
              <RichTextRenderer
                content={notification.rich_content}
                className="text-xs text-gray-600"
              />
            </div>
          )}

        {/* Row 4: Metadata (e.g. Report Name, Retest Name) */}
        {(notification.payload || reportTitle || retestTitle) && (
          <div className="text-xs text-gray-500 mt-3 font-semibold flex items-center gap-1.5">
            {isRetest ? (
              <span>Retest: {retestTitle || `#${notification.payload}`}</span>
            ) : (isReportNotification || notification.type === NotificationType.Collaboration || (notification.title && notification.title.toLowerCase().includes("report"))) ? (
              <span>Report: {reportTitle || `#${notification.payload}`}</span>
            ) : notification.type === NotificationType.Program ? (
              (() => {
                const reportId = getPayloadId(notification.payload, "report_id");
                if (reportId && reportId !== String(notification.payload)) {
                  return <span>Report: {reportTitle || `#${reportId}`}</span>;
                }
                return <span>Program ID: #{getPayloadId(notification.payload, "program_id")}</span>;
              })()
            ) : null}
          </div>
        )}

        {/* Row 5: Actions / CTAs */}
        <div className="flex items-center gap-3 mt-4">
          {/* Default Dynamic Actions */}
          {notification.actions && notification.actions.length > 0 ? (
            notification.actions.map((action, idx) => (
              <button
                key={idx}
                className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors border ${action.style === "primary"
                  ? "bg-[#010D2C] text-white hover:opacity-90 border-[#010D2C]"
                  : action.style === "danger"
                    ? "bg-red-600 text-white hover:bg-red-700 border-red-600"
                    : action.style === "success"
                      ? "bg-[#58CC02] text-white hover:bg-[#58CC02]/90 border-[#58CC02]"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                  }`}
                onClick={(e) => {
                  e.preventDefault();
                  if (!notification.read) {
                    onMarkRead(notification.id);
                  }
                  if (action.url) {
                    navigate(action.url);
                  } else {
                    navigate(detailUrl);
                  }
                }}
              >
                {action.label}
              </button>
            ))
          ) : (
            /* Contextual hardcoded actions */
            <>
              {isReportNotification && notification.type !== NotificationType.Comments && (
                <>
                  <button
                    onClick={handlePrimaryAction}
                    className="px-4 py-2 text-xs font-bold rounded-lg bg-[#010D2C] text-white hover:opacity-90 transition-colors"
                  >
                    {isRetest ? "View Retest" : "View Report"}
                  </button>
                </>
              )}
              {notification.type === NotificationType.Comments && (
                <>
                  <button
                    onClick={handlePrimaryAction}
                    className="px-4 py-2 text-xs font-bold rounded-lg bg-[#010D2C] text-white hover:opacity-90 transition-colors"
                  >
                    View Comment
                  </button>
                  <button
                    onClick={handleReplyAction}
                    className="px-4 py-2 text-xs font-bold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Reply
                  </button>
                </>
              )}
              {notification.type === NotificationType.Program && (
                (() => {
                  const reportId = getPayloadId(notification.payload, "report_id");
                  if (reportId && reportId !== String(notification.payload)) {
                    return (
                      <button
                        onClick={handlePrimaryAction}
                        className="px-4 py-2 text-xs font-bold rounded-lg bg-[#010D2C] text-white hover:opacity-90 transition-colors"
                      >
                        View Report
                      </button>
                    );
                  }
                  return (
                    <button
                      onClick={handlePrimaryAction}
                      className="px-4 py-2 text-xs font-bold rounded-lg bg-[#010D2C] text-white hover:opacity-90 transition-colors"
                    >
                      View Program
                    </button>
                  );
                })()
              )}
              {!isReportNotification && notification.type !== NotificationType.Program && (
                <button
                  onClick={handlePrimaryAction}
                  className="px-4 py-2 text-xs font-bold rounded-lg bg-[#010D2C] text-white hover:opacity-90 transition-colors"
                >
                  {isRetest ? "View Retest" : (notification.type === NotificationType.Collaboration || (notification.title && notification.title.toLowerCase().includes("report")) ? "View Report" : "View Details")}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Delete Notification"
        message="Are you sure you want to delete this notification? This action cannot be undone."
        confirmLabel="Yes, Delete"
        confirmStyle="danger"
        onConfirm={() => {
          onDelete(notification.id);
          setShowDeleteConfirm(false);
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default NotificationCard;
